import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { CommunicationLanguage, isAdminUser } from '../types/auth';
import {
  InstitutionDto,
  InstitutionMembershipCategory,
  AdministrativeStatus,
  InstitutionContactPreference,
  memberCardsForCategory,
  normalizeCommunicationLanguage,
} from '../types/institution';
import { Title } from '../types/member';
import { committeeService } from '../services/committees';
import { authService } from '../services/auth';
import { adminService } from '../services/admin';
import { applicationStatusService } from '../services/applicationStatus';
import { InternationalCommittee } from '../types/committee';
import FormField from '../components/common/FormField';
import FileUpload from '../components/common/FileUpload';
import PasswordVisibilityToggle from '../components/common/PasswordVisibilityToggle';
import { focusFormError } from '../utils/focusFormError';
import Modal from '../components/common/Modal';
import BilingualDoubleInput from '../components/registration/BilingualDoubleInput';
import { CountrySelector } from '../components/registration/CountrySelector';
import PhoneWithDialCode from '../components/registration/PhoneWithDialCode';
import AuthWizardShell from '../components/registration/AuthWizardShell';
import {
  ICOM_INSTITUTION_TYPE_KEYS,
  isIcomInstitutionTypeKey,
  type IcomInstitutionTypeKey,
} from '../data/icomInstitutionTypes';
import WizardStepPanel from '../components/registration/WizardStepPanel';
import FormAlert from '../components/registration/FormAlert';
import WizardProgress from '../components/registration/WizardProgress';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Text } from '../design-system/components/Typography';
import { FormSection } from '../design-system/patterns/FormSection';
import {
  Form,
  FormRow,
  FormRowCityPostal,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
} from '../design-system/patterns/FormLayout';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { animations } from '../design-system/tokens/animations';
import * as validation from '../utils/validation';
import { getApiErrorMessage, getValidationErrorMessage } from '../utils/apiErrors';
import { uniqueInternationalCommittees } from '../utils/committeeUtils';


const hasBilingualText = (en?: string, gr?: string): boolean =>
  !!(en?.trim() || gr?.trim());

/** True when the secondary address block has any usable content. */
const hasSecondaryAddressContent = (data: {
  secondaryAddressEn?: string;
  secondaryAddressGr?: string;
  secondaryCityEn?: string;
  secondaryCityGr?: string;
  secondaryCountryEn?: string;
  secondaryCountryGr?: string;
  secondaryPostalCodeEn?: string;
  secondaryPostalCodeGr?: string;
}): boolean =>
  !!(
    data.secondaryAddressEn?.trim() ||
    data.secondaryAddressGr?.trim() ||
    data.secondaryCityEn?.trim() ||
    data.secondaryCityGr?.trim() ||
    data.secondaryCountryEn?.trim() ||
    data.secondaryCountryGr?.trim() ||
    data.secondaryPostalCodeEn?.trim() ||
    data.secondaryPostalCodeGr?.trim()
  );

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};
  transition: ${animations.transition(['color', 'text-decoration'], animations.duration.fast, animations.easing.easeOut)};
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const SectionHeading = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing[3]};
  margin-top: ${spacing[6]};
  padding-bottom: ${spacing[2]};
  border-bottom: ${borders.width.thin} solid ${props => props.theme.colors.borderLight || props.theme.colors.border};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
`;

const IcomTypeFieldset = styled.fieldset`
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  padding: ${spacing[4]} ${spacing[5]};
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const IcomTypeLegend = styled.legend`
  padding: 0 ${spacing[2]};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
`;

const IcomTypeIntro = styled(Text)`
  margin: 0 0 ${spacing[1]} 0;
`;

const IcomTypeOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
`;

const IcomTypeRadio = styled.input`
  margin-top: ${spacing[1]};
  flex-shrink: 0;
  width: ${spacing[4]};
  height: ${spacing[4]};
  cursor: pointer;
  accent-color: ${props => props.theme.colors.primary};
`;

const FieldErrorText = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

const StepPanel = styled.div`
  outline: none;
`;

const ButtonGroupRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: ${spacing[4]};
`;

const NavSpacer = styled.span`
  display: inline-block;
  min-width: 1px;
`;

const RepCard = styled(Card)`
  padding: ${spacing[5]};
  margin-bottom: ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background || props.theme.colors.surface};
`;

const RepCardTitle = styled.h4`
  color: ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
  margin: 0 0 ${spacing[4]} 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
`;

const SpacedBlock = styled.div`
  margin-bottom: ${spacing[4]};
`;

const RepresentativesBlock = styled.div`
  margin-top: ${spacing[4]};
  padding: ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
`;

const RepresentativesBlockTitle = styled(SectionHeading)`
  margin-top: 0;
  margin-bottom: ${spacing[2]};
`;

const CommitteesList = styled.ul`
  margin: ${spacing[4]} 0 0 0;
  padding-left: ${spacing[6]};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
`;

const GdprTabs = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
`;

const GdprTab = styled.button<{ $active?: boolean }>`
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borders.radius.md};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.surface : props.theme.colors.text};
  cursor: pointer;
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.icomBlue || props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const StatementBlock = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surface};
  border-radius: ${borders.radius.lg};
  margin-bottom: ${spacing[6]};
  font-size: ${typography.fontSize.sm};
  line-height: 1.7;
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
`;

const CONSENT_ERROR_KEYS = ['acceptedEthics', 'acceptedGdpr', 'acceptedDataTransfer', 'acceptedStatement'];
const FINAL_STEP_SUBMIT_GRACE_MS = 400;

function createEmptyInstitutionForm(communicationLanguage: CommunicationLanguage) {
  return {
    email: '',
    password: '',
    confirmPassword: '',
    communicationLanguage,
    institutionNameEn: '',
    institutionNameGr: '',
    institutionName: '',
    foundationDate: '',
    website: '',
    institutionAddressEn: '',
    institutionAddressGr: '',
    institutionCityEn: '',
    institutionCityGr: '',
    institutionPostalCodeEn: '',
    institutionPostalCodeGr: '',
    institutionCountryEn: '',
    institutionCountryGr: '',
    institutionPhone: '',
    institutionEmail: '',
    secondaryAddressEn: '',
    secondaryAddressGr: '',
    secondaryCityEn: '',
    secondaryCityGr: '',
    secondaryPostalCodeEn: '',
    secondaryPostalCodeGr: '',
    secondaryCountryEn: '',
    secondaryCountryGr: '',
    directorTitle: '' as string,
    directorFirstNameEn: '',
    directorFirstNameGr: '',
    directorLastNameEn: '',
    directorLastNameGr: '',
    directorDateOfBirth: '',
    directorEmail: '',
    directorPhone: '',
    icomRepresentativeTitle: '' as string,
    icomRepresentativeFirstNameEn: '',
    icomRepresentativeFirstNameGr: '',
    icomRepresentativeLastNameEn: '',
    icomRepresentativeLastNameGr: '',
    icomRepresentativeDateOfBirth: '',
    icomRepresentativePosition: '',
    icomRepresentativeEmail: '',
    icomRepresentativePhone: '',
    icomContactPreference: '' as string,
    icomRepresentativeCommunicationLanguage: CommunicationLanguage.English,
    membershipCategory: InstitutionMembershipCategory.ActiveI,
    institutionType: '' as IcomInstitutionTypeKey | '',
    administrativeStatus: AdministrativeStatus.Public,
    rep1Title: '' as string,
    rep1FirstNameEn: '',
    rep1FirstNameGr: '',
    rep1LastNameEn: '',
    rep1LastNameGr: '',
    rep1DateOfBirth: '',
    rep1PositionEn: '',
    rep1PositionGr: '',
    rep1Email: '',
    rep1Phone: '',
    rep1CommitteeId: '',
    rep2Title: '' as string,
    rep2FirstNameEn: '',
    rep2FirstNameGr: '',
    rep2LastNameEn: '',
    rep2LastNameGr: '',
    rep2DateOfBirth: '',
    rep2PositionEn: '',
    rep2PositionGr: '',
    rep2Email: '',
    rep2Phone: '',
    rep2CommitteeId: '',
    rep3Title: '' as string,
    rep3FirstNameEn: '',
    rep3FirstNameGr: '',
    rep3LastNameEn: '',
    rep3LastNameGr: '',
    rep3DateOfBirth: '',
    rep3PositionEn: '',
    rep3PositionGr: '',
    rep3Email: '',
    rep3Phone: '',
    rep3CommitteeId: '',
    foundationAct: null as File | null,
    declarationDate: '',
    acceptedEthics: false,
    acceptedGdpr: false,
    acceptedDataTransfer: false,
    acceptedStatement: false,
  };
}

function institutionDtoToFormData(
  inst: InstitutionDto,
  communicationLanguage: CommunicationLanguage
) {
  const base = createEmptyInstitutionForm(communicationLanguage);
  const reps = (inst.committees ?? []).slice(0, 3);
  const mapRep = (index: number) => {
    const c = reps[index];
    if (!c) return {};
    const prefix = `rep${index + 1}` as const;
    return {
      [`${prefix}Title`]: c.representativeTitle != null ? String(c.representativeTitle) : '',
      [`${prefix}FirstNameEn`]: c.name?.firstNameEn ?? '',
      [`${prefix}FirstNameGr`]: c.name?.firstNameGr ?? '',
      [`${prefix}LastNameEn`]: c.name?.lastNameEn ?? '',
      [`${prefix}LastNameGr`]: c.name?.lastNameGr ?? '',
      [`${prefix}DateOfBirth`]: c.representativeDateOfBirth
        ? String(c.representativeDateOfBirth).slice(0, 10)
        : '',
      [`${prefix}PositionEn`]: c.representativePositionEn ?? c.representativePosition ?? '',
      [`${prefix}PositionGr`]: c.representativePositionGr ?? '',
      [`${prefix}Email`]: c.representativeEmail ?? '',
      [`${prefix}Phone`]: c.representativePhone ?? '',
      [`${prefix}CommitteeId`]: c.committeeId ?? '',
    };
  };

  return {
    ...base,
    email: inst.institutionEmail ?? '',
    institutionNameEn: inst.institutionName?.en ?? '',
    institutionNameGr: inst.institutionName?.gr ?? '',
    foundationDate: inst.foundationDate ? String(inst.foundationDate).slice(0, 10) : '',
    website: inst.website ?? '',
    institutionAddressEn: inst.primaryAddress?.addressEn ?? '',
    institutionAddressGr: inst.primaryAddress?.addressGr ?? '',
    institutionCityEn: inst.primaryAddress?.cityEn ?? '',
    institutionCityGr: inst.primaryAddress?.cityGr ?? '',
    institutionPostalCodeEn: inst.primaryAddress?.postalCodeEn ?? '',
    institutionPostalCodeGr: inst.primaryAddress?.postalCodeGr ?? '',
    institutionCountryEn: inst.primaryAddress?.countryEn ?? '',
    institutionCountryGr: inst.primaryAddress?.countryGr ?? '',
    institutionPhone: inst.institutionPhone ?? '',
    institutionEmail: inst.institutionEmail ?? '',
    secondaryAddressEn: inst.secondaryAddress?.addressEn ?? '',
    secondaryAddressGr: inst.secondaryAddress?.addressGr ?? '',
    secondaryCityEn: inst.secondaryAddress?.cityEn ?? '',
    secondaryCityGr: inst.secondaryAddress?.cityGr ?? '',
    secondaryPostalCodeEn: inst.secondaryAddress?.postalCodeEn ?? '',
    secondaryPostalCodeGr: inst.secondaryAddress?.postalCodeGr ?? '',
    secondaryCountryEn: inst.secondaryAddress?.countryEn ?? '',
    secondaryCountryGr: inst.secondaryAddress?.countryGr ?? '',
    directorTitle: inst.directorTitle != null ? String(inst.directorTitle) : '',
    directorFirstNameEn: inst.directorName?.firstNameEn ?? '',
    directorFirstNameGr: inst.directorName?.firstNameGr ?? '',
    directorLastNameEn: inst.directorName?.lastNameEn ?? '',
    directorLastNameGr: inst.directorName?.lastNameGr ?? '',
    directorDateOfBirth: inst.directorDateOfBirth
      ? String(inst.directorDateOfBirth).slice(0, 10)
      : '',
    directorEmail: inst.directorEmail ?? '',
    directorPhone: inst.directorPhone ?? '',
    icomRepresentativeTitle:
      inst.icomRepresentativeTitle != null ? String(inst.icomRepresentativeTitle) : '',
    icomRepresentativeFirstNameEn: inst.icomRepresentativeName?.firstNameEn ?? '',
    icomRepresentativeFirstNameGr: inst.icomRepresentativeName?.firstNameGr ?? '',
    icomRepresentativeLastNameEn: inst.icomRepresentativeName?.lastNameEn ?? '',
    icomRepresentativeLastNameGr: inst.icomRepresentativeName?.lastNameGr ?? '',
    icomRepresentativeDateOfBirth: inst.icomRepresentativeDateOfBirth
      ? String(inst.icomRepresentativeDateOfBirth).slice(0, 10)
      : '',
    icomRepresentativePosition: inst.icomRepresentativePosition ?? '',
    icomRepresentativeEmail: inst.icomRepresentativeEmail ?? '',
    icomRepresentativePhone: inst.icomRepresentativePhone ?? '',
    icomContactPreference:
      inst.icomContactPreference != null ? String(inst.icomContactPreference) : '',
    icomRepresentativeCommunicationLanguage:
      normalizeCommunicationLanguage(inst.icomRepresentativeCommunicationLanguage) ??
      CommunicationLanguage.English,
    membershipCategory: inst.membershipCategory ?? InstitutionMembershipCategory.ActiveI,
    institutionType: (() => {
      const raw = inst.institutionType?.[0] ?? '';
      return isIcomInstitutionTypeKey(raw) ? raw : ('' as const);
    })(),
    administrativeStatus: inst.administrativeStatus ?? AdministrativeStatus.Public,
    ...mapRep(0),
    ...mapRep(1),
    ...mapRep(2),
    acceptedEthics: true,
    acceptedGdpr: true,
    acceptedDataTransfer: true,
    acceptedStatement: true,
  };
}

const RegisterInstitution: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [gdprVariant, setGdprVariant] = useState<'with' | 'without'>('with');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const { success: showSuccess, error: showError } = useToast();

  const mode = searchParams.get('mode');
  const referenceParam = searchParams.get('reference')?.trim() || '';
  const isResubmit = mode === 'resubmit';
  const isAdminCreate = mode === 'admin';
  const skipPassword = isResubmit;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modeLoading, setModeLoading] = useState(isResubmit || isAdminCreate);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [committees, setCommittees] = useState<InternationalCommittee[]>([]);
  const [committeesLoadError, setCommitteesLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [committeesModalOpen, setCommitteesModalOpen] = useState(false);
  const [gdprModalOpen, setGdprModalOpen] = useState(false);
  const [statementGdprModalOpen, setStatementGdprModalOpen] = useState(false);
  const [hasAttemptedFinalSubmit, setHasAttemptedFinalSubmit] = useState(false);
  const [applicantNote, setApplicantNote] = useState('');
  const [resubmitReference, setResubmitReference] = useState(referenceParam);
  const lastStepEnteredAtRef = useRef(0);
  const resubmitLoadedRef = useRef(false);
  
  // Get current language and map to CommunicationLanguage
  const getCurrentCommunicationLanguage = (): CommunicationLanguage => {
    const lang = i18n.language.toLowerCase();
    if (lang.startsWith('el') || lang.startsWith('gr')) return CommunicationLanguage.Greek;
    if (lang.startsWith('fr')) return CommunicationLanguage.French;
    if (lang.startsWith('es')) return CommunicationLanguage.Spanish;
    return CommunicationLanguage.English;
  };

  const [formData, setFormData] = useState(() =>
    createEmptyInstitutionForm(getCurrentCommunicationLanguage())
  );

  const DRAFT_STORAGE_KEY = 'icom-register-institution-draft';

  useEffect(() => {
    if (isAdminCreate && (!isAuthenticated || !isAdminUser(user))) {
      navigate('/login', { replace: true });
      return;
    }
    if (isAdminCreate) {
      setFormData(createEmptyInstitutionForm(getCurrentCommunicationLanguage()));
      setModeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminCreate, isAuthenticated, user]);

  useEffect(() => {
    if (isResubmit || isAdminCreate) return;
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { step?: number; formData?: Record<string, unknown> };
        if (parsed.step != null && parsed.formData && typeof parsed.formData === 'object') {
          setStep(parsed.step);
          setFormData(prev => ({ ...prev, ...parsed.formData, foundationAct: null } as typeof prev));
        }
      }
    } catch {
      // ignore invalid draft
    }
  }, [isResubmit, isAdminCreate]);

  useEffect(() => {
    if (step === 4 || isResubmit) loadCommittees();
  }, [step, isResubmit]);

  useEffect(() => {
    if (!isResubmit || resubmitLoadedRef.current) return;
    const loadEditable = async () => {
      if (!referenceParam) {
        showError(t('applicationStatus.enterReference'));
        navigate('/application-status', { replace: true });
        return;
      }
      setModeLoading(true);
      try {
        const editable = await applicationStatusService.getEditable(referenceParam);
        if (!editable.canEdit || (editable.type || '').toLowerCase() !== 'institution' || !editable.institution) {
          showError(t('applicationStatus.resubmitError'));
          navigate(`/application-status?reference=${encodeURIComponent(referenceParam)}`, {
            replace: true,
          });
          return;
        }
        setResubmitReference(editable.applicationReferenceNumber);
        setFormData(institutionDtoToFormData(editable.institution, getCurrentCommunicationLanguage()));
        resubmitLoadedRef.current = true;
      } catch (err) {
        showError(err instanceof Error ? err.message : t('applicationStatus.resubmitError'));
        navigate(`/application-status?reference=${encodeURIComponent(referenceParam)}`, {
          replace: true,
        });
      } finally {
        setModeLoading(false);
      }
    };
    loadEditable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResubmit, referenceParam]);

  useEffect(() => {
    if (isResubmit || isAdminCreate) return;
    if (step < 1) return;
    try {
      const toSave = { step, formData: { ...formData, foundationAct: null } };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(toSave));
    } catch {
      // ignore quota etc.
    }
  }, [step, formData, isResubmit, isAdminCreate]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, communicationLanguage: getCurrentCommunicationLanguage() }));
  }, [i18n.language]);

  // Re-translate validation summary/inline errors when the UI language changes.
  // Never re-run consent rules on the final step until the user submits.
  useEffect(() => {
    if (!error && Object.keys(fieldErrors).length === 0) return;
    if (step === 5) {
      if (!hasAttemptedFinalSubmit) return;
      setFieldErrors(prev => {
        const next = { ...prev };
        if (next.acceptedEthics) next.acceptedEthics = t('register.ethicsRequired');
        if (next.acceptedGdpr) next.acceptedGdpr = t('register.gdprRequired');
        if (next.acceptedDataTransfer) {
          next.acceptedDataTransfer = t('errors.api.dataTransferRequired');
        }
        if (next.acceptedStatement) {
          next.acceptedStatement = t('errors.api.statementRequired');
        }
        return next;
      });
      if (error) setError(t('register.formErrorsBelow'));
      return;
    }
    // Retranslate existing errors only; do not re-run validation on language change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const loadCommittees = async () => {
    setCommitteesLoadError(null);
    try {
      const data = await committeeService.getAllCommittees();
      setCommittees(uniqueInternationalCommittees(data.filter(c => c.isActive)));
    } catch (err) {
      console.error('Failed to load committees:', err);
      setCommitteesLoadError(t('register.committeesLoadError'));
    }
  };

  const validateField = (fieldName: string, value: any): string | undefined => {
    switch (fieldName) {
      case 'email':
        return validation.validateEmail(value).error;
      case 'password':
        return validation.validatePassword(value, 6).error;
      case 'confirmPassword':
        return validation.validatePasswordConfirmation(formData.password, value).error;
      case 'institutionName':
      case 'institutionNameEn':
      case 'institutionNameGr':
        return validation.validateRequired(value, 'Institution name').error;
      case 'institutionPhone':
      case 'directorPhone':
      case 'icomRepresentativePhone':
        return validation.validatePhone(value, false).error;
      case 'institutionEmail':
      case 'directorEmail':
      case 'icomRepresentativeEmail':
        if (value && value.trim() !== '') {
          return validation.validateEmail(value).error;
        }
        return undefined;
      case 'rep1Email':
      case 'rep2Email':
      case 'rep3Email':
        if (!value || value.trim() === '') {
          return undefined;
        }
        return validation.validateEmail(value).error;
      case 'rep1Phone':
      case 'rep2Phone':
      case 'rep3Phone':
        return validation.validatePhone(value, false).error;
      case 'website':
        if (value && value.trim() !== '') {
          return validation.validateUrl(value, false).error;
        }
        return undefined;
      case 'foundationDate':
      case 'directorDateOfBirth':
      case 'icomRepresentativeDateOfBirth':
      case 'rep1DateOfBirth':
      case 'rep2DateOfBirth':
      case 'rep3DateOfBirth':
        return validation.validateDate(value, false).error;
      default:
        return undefined;
    }
  };

  const getRepresentativeValues = (prefix: 'rep1' | 'rep2' | 'rep3') => {
    const rep = formData;
    if (prefix === 'rep1') {
      return {
        title: rep.rep1Title,
        firstEn: rep.rep1FirstNameEn,
        firstGr: rep.rep1FirstNameGr,
        lastEn: rep.rep1LastNameEn,
        lastGr: rep.rep1LastNameGr,
        dob: rep.rep1DateOfBirth,
        positionEn: rep.rep1PositionEn,
        positionGr: rep.rep1PositionGr,
        email: rep.rep1Email,
        phone: rep.rep1Phone,
        committeeId: rep.rep1CommitteeId,
      };
    }
    if (prefix === 'rep2') {
      return {
        title: rep.rep2Title,
        firstEn: rep.rep2FirstNameEn,
        firstGr: rep.rep2FirstNameGr,
        lastEn: rep.rep2LastNameEn,
        lastGr: rep.rep2LastNameGr,
        dob: rep.rep2DateOfBirth,
        positionEn: rep.rep2PositionEn,
        positionGr: rep.rep2PositionGr,
        email: rep.rep2Email,
        phone: rep.rep2Phone,
        committeeId: rep.rep2CommitteeId,
      };
    }
    return {
      title: rep.rep3Title,
      firstEn: rep.rep3FirstNameEn,
      firstGr: rep.rep3FirstNameGr,
      lastEn: rep.rep3LastNameEn,
      lastGr: rep.rep3LastNameGr,
      dob: rep.rep3DateOfBirth,
      positionEn: rep.rep3PositionEn,
      positionGr: rep.rep3PositionGr,
      email: rep.rep3Email,
      phone: rep.rep3Phone,
      committeeId: rep.rep3CommitteeId,
    };
  };

  /** Empty representatives are ignored; partially filled ones must be completed. */
  const isRepresentativeStarted = (prefix: 'rep1' | 'rep2' | 'rep3'): boolean => {
    const v = getRepresentativeValues(prefix);
    return !!(
      v.title ||
      v.firstEn?.trim() ||
      v.firstGr?.trim() ||
      v.lastEn?.trim() ||
      v.lastGr?.trim() ||
      v.dob ||
      v.positionEn?.trim() ||
      v.positionGr?.trim() ||
      v.email?.trim() ||
      v.phone?.trim() ||
      v.committeeId
    );
  };

  const validateRepresentative = (
    prefix: 'rep1' | 'rep2' | 'rep3',
    errors: Record<string, string>,
  ): boolean => {
    if (!isRepresentativeStarted(prefix)) {
      return true;
    }

    let isValid = true;
    const v = getRepresentativeValues(prefix);
    const titleKey = `${prefix}Title`;
    const firstKey = `${prefix}FirstName`;
    const lastKey = `${prefix}LastName`;
    const dobKey = `${prefix}DateOfBirth`;
    const positionKey = `${prefix}Position`;
    const emailKey = `${prefix}Email`;
    const phoneKey = `${prefix}Phone`;
    const committeeKey = `${prefix}CommitteeId`;

    if (!v.title) {
      errors[titleKey] = t('register.titleRequired');
      isValid = false;
    }
    if (!hasBilingualText(v.firstEn, v.firstGr)) {
      errors[firstKey] = t('register.firstNameRequired');
      isValid = false;
    }
    const lastNameErrorKey = validation.bilingualLastNameErrorKey(v.lastEn, v.lastGr);
    if (lastNameErrorKey) {
      errors[lastKey] = t(lastNameErrorKey);
      isValid = false;
    }
    const dobError = validateField(dobKey, v.dob);
    if (dobError) {
      errors[dobKey] = getValidationErrorMessage(dobError, t);
      isValid = false;
    }
    if (!hasBilingualText(v.positionEn, v.positionGr)) {
      errors[positionKey] = t('register.positionRequired');
      isValid = false;
    }
    if (!v.email?.trim()) {
      errors[emailKey] = t('validation.emailRequired');
      isValid = false;
    } else {
      const emailError = validateField(emailKey, v.email);
      if (emailError) {
        errors[emailKey] = getValidationErrorMessage(emailError, t);
        isValid = false;
      }
    }
    const phoneError = validateField(phoneKey, v.phone);
    if (phoneError) {
      errors[phoneKey] = getValidationErrorMessage(phoneError, t);
      isValid = false;
    }
    if (!v.committeeId) {
      errors[committeeKey] = t('register.committees.mainRequired');
      isValid = false;
    }

    return isValid;
  };

  const validateStep = (
    stepToValidate: number = step,
    options?: { includeConsent?: boolean }
  ): boolean => {
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;
    const includeConsent = options?.includeConsent === true;

    if (stepToValidate === 1) {
      if (!skipPassword) {
        const emailError = validateField('email', formData.email);
        if (emailError) {
          errors.email = getValidationErrorMessage(emailError, t);
          isValid = false;
        }
        const passwordError = validateField('password', formData.password);
        if (passwordError) {
          errors.password = getValidationErrorMessage(passwordError, t);
          isValid = false;
        }
        const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
        if (confirmPasswordError) {
          errors.confirmPassword = getValidationErrorMessage(confirmPasswordError, t);
          isValid = false;
        }
      }
      const hasName = hasBilingualText(
        formData.institutionNameEn,
        formData.institutionNameGr,
      ) || !!formData.institutionName?.trim();
      if (!hasName) {
        const nameMsg = t('register.institutionNameRequired');
        errors.institutionName = nameMsg;
        errors.institutionNameEn = nameMsg;
        errors.institutionNameGr = nameMsg;
        isValid = false;
      }
      if (!formData.institutionType) {
        errors.institutionType = t('institution.icomType.required');
        isValid = false;
      }
    }

    if (stepToValidate === 2) {
      if (!hasBilingualText(formData.institutionAddressEn, formData.institutionAddressGr)) {
        errors.institutionAddress = t('register.addressRequired');
        isValid = false;
      }
      if (!hasBilingualText(formData.institutionCityEn, formData.institutionCityGr)) {
        errors.institutionCity = t('register.cityRequired');
        isValid = false;
      }
      if (!hasBilingualText(formData.institutionCountryEn, formData.institutionCountryGr)) {
        errors.institutionCountry = t('register.countryRequired');
        isValid = false;
      }
      const phoneError = validateField('institutionPhone', formData.institutionPhone);
      if (!formData.institutionPhone?.trim()) {
        errors.institutionPhone = getValidationErrorMessage(
          validation.validatePhone('', true).error || 'Phone number is required',
          t,
        );
        isValid = false;
      } else if (phoneError) {
        errors.institutionPhone = getValidationErrorMessage(phoneError, t);
        isValid = false;
      }
      const institutionEmailError = validateField('institutionEmail', formData.institutionEmail);
      if (institutionEmailError) {
        errors.institutionEmail = getValidationErrorMessage(institutionEmailError, t);
        isValid = false;
      } else if (!formData.institutionEmail?.trim()) {
        errors.institutionEmail = t('validation.emailRequired');
        isValid = false;
      }
    }

    if (stepToValidate === 3) {
      if (!formData.directorTitle) {
        errors.directorTitle = t('register.titleRequired');
        isValid = false;
      }
      if (!hasBilingualText(formData.directorFirstNameEn, formData.directorFirstNameGr)) {
        errors.directorFirstName = t('register.firstNameRequired');
        isValid = false;
      }
      const directorLastNameErrorKey = validation.bilingualLastNameErrorKey(
        formData.directorLastNameEn,
        formData.directorLastNameGr
      );
      if (directorLastNameErrorKey) {
        errors.directorLastName = t(directorLastNameErrorKey);
        isValid = false;
      }
      const directorDobError = validateField('directorDateOfBirth', formData.directorDateOfBirth);
      if (directorDobError) {
        errors.directorDateOfBirth = getValidationErrorMessage(directorDobError, t);
        isValid = false;
      }
      if (!formData.icomRepresentativeTitle) {
        errors.icomRepresentativeTitle = t('register.titleRequired');
        isValid = false;
      }
      if (!hasBilingualText(formData.icomRepresentativeFirstNameEn, formData.icomRepresentativeFirstNameGr)) {
        errors.icomRepresentativeFirstName = t('register.firstNameRequired');
        isValid = false;
      }
      const icomRepLastNameErrorKey = validation.bilingualLastNameErrorKey(
        formData.icomRepresentativeLastNameEn,
        formData.icomRepresentativeLastNameGr
      );
      if (icomRepLastNameErrorKey) {
        errors.icomRepresentativeLastName = t(icomRepLastNameErrorKey);
        isValid = false;
      }
      const repDobError = validateField('icomRepresentativeDateOfBirth', formData.icomRepresentativeDateOfBirth);
      if (repDobError) {
        errors.icomRepresentativeDateOfBirth = getValidationErrorMessage(repDobError, t);
        isValid = false;
      }
      if (!formData.icomRepresentativePosition?.trim()) {
        errors.icomRepresentativePosition = t('register.positionRequired');
        isValid = false;
      }
      const icomEmailError = validateField('icomRepresentativeEmail', formData.icomRepresentativeEmail);
      if (icomEmailError) {
        errors.icomRepresentativeEmail = getValidationErrorMessage(icomEmailError, t);
        isValid = false;
      } else if (!formData.icomRepresentativeEmail?.trim()) {
        errors.icomRepresentativeEmail = t('validation.emailRequired');
        isValid = false;
      }
      if (!formData.icomContactPreference) {
        errors.icomContactPreference = t('register.contactPreferenceRequired');
        isValid = false;
      } else if (
        formData.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
        !hasSecondaryAddressContent(formData)
      ) {
        errors.icomContactPreference = t('register.secondaryAddressRequiredForCorrespondence', {
          defaultValue:
            'Add a secondary address before selecting it for correspondence, or choose the primary address.',
        });
        isValid = false;
      }
    }

    if (stepToValidate === 4) {
      if (committeesLoadError) {
        setError(committeesLoadError);
        isValid = false;
      }
      isValid = validateRepresentative('rep1', errors) && isValid;
      isValid = validateRepresentative('rep2', errors) && isValid;
      isValid = validateRepresentative('rep3', errors) && isValid;
      const committeeIds = [formData.rep1CommitteeId, formData.rep2CommitteeId, formData.rep3CommitteeId].filter(
        id => !!id
      );
      if (committeeIds.length > 1 && new Set(committeeIds).size !== committeeIds.length) {
        errors.representativesCommittees = t('register.representativesDuplicateCommittees');
        setError(t('register.representativesDuplicateCommittees'));
        isValid = false;
      }
    }

    if (stepToValidate === 5 && includeConsent) {
      if (!formData.foundationAct) {
        errors.foundationAct = t('register.foundationActRequired');
        isValid = false;
      }
      if (!formData.acceptedEthics) {
        errors.acceptedEthics = t('register.ethicsRequired');
        isValid = false;
      }
      if (!formData.acceptedGdpr) {
        errors.acceptedGdpr = t('register.gdprRequired');
        isValid = false;
      }
      if (!formData.acceptedDataTransfer) {
        errors.acceptedDataTransfer = t('errors.api.dataTransferRequired');
        isValid = false;
      }
      if (!formData.acceptedStatement) {
        errors.acceptedStatement = t('errors.api.statementRequired');
        isValid = false;
      }
      if (!isValid) {
        setError(t('register.formErrorsBelow'));
      }
    }

    setFieldErrors(errors);
    return isValid;
  };

  /** Validates all steps for final submit (step 5). */
  const validateAllForSubmit = (): boolean => {
    setError(null);
    const errors: Record<string, string> = {};
    let isValid = true;

    for (let s = 1; s <= 5; s += 1) {
      const stepErrors: Record<string, string> = {};
      let stepValid = true;

      if (s === 1) {
        if (!skipPassword) {
          const emailError = validateField('email', formData.email);
          if (emailError) {
            stepErrors.email = getValidationErrorMessage(emailError, t);
            stepValid = false;
          }
          const passwordError = validateField('password', formData.password);
          if (passwordError) {
            stepErrors.password = getValidationErrorMessage(passwordError, t);
            stepValid = false;
          }
          const confirmPasswordError = validateField('confirmPassword', formData.confirmPassword);
          if (confirmPasswordError) {
            stepErrors.confirmPassword = getValidationErrorMessage(confirmPasswordError, t);
            stepValid = false;
          }
        }
        if (!hasBilingualText(formData.institutionNameEn, formData.institutionNameGr) && !formData.institutionName?.trim()) {
          const nameMsg = t('register.institutionNameRequired');
          stepErrors.institutionName = nameMsg;
          stepErrors.institutionNameEn = nameMsg;
          stepErrors.institutionNameGr = nameMsg;
          stepValid = false;
        }
        if (!formData.institutionType) {
          stepErrors.institutionType = t('institution.icomType.required');
          stepValid = false;
        }
      }

      if (s === 2) {
        if (!hasBilingualText(formData.institutionAddressEn, formData.institutionAddressGr)) {
          stepErrors.institutionAddress = t('register.addressRequired');
          stepValid = false;
        }
        if (!hasBilingualText(formData.institutionCityEn, formData.institutionCityGr)) {
          stepErrors.institutionCity = t('register.cityRequired');
          stepValid = false;
        }
        if (!hasBilingualText(formData.institutionCountryEn, formData.institutionCountryGr)) {
          stepErrors.institutionCountry = t('register.countryRequired');
          stepValid = false;
        }
        if (!formData.institutionPhone?.trim()) {
          stepErrors.institutionPhone = t('validation.phoneRequired');
          stepValid = false;
        } else {
          const phoneError = validateField('institutionPhone', formData.institutionPhone);
          if (phoneError) {
            stepErrors.institutionPhone = getValidationErrorMessage(phoneError, t);
            stepValid = false;
          }
        }
        if (!formData.institutionEmail?.trim()) {
          stepErrors.institutionEmail = t('validation.emailRequired');
          stepValid = false;
        } else {
          const institutionEmailError = validateField('institutionEmail', formData.institutionEmail);
          if (institutionEmailError) {
            stepErrors.institutionEmail = getValidationErrorMessage(institutionEmailError, t);
            stepValid = false;
          }
        }
      }

      if (s === 3) {
        if (!formData.directorTitle) stepErrors.directorTitle = t('register.titleRequired');
        if (!hasBilingualText(formData.directorFirstNameEn, formData.directorFirstNameGr)) {
          stepErrors.directorFirstName = t('register.firstNameRequired');
        }
        const directorLastNameErrorKey = validation.bilingualLastNameErrorKey(
          formData.directorLastNameEn,
          formData.directorLastNameGr
        );
        if (directorLastNameErrorKey) {
          stepErrors.directorLastName = t(directorLastNameErrorKey);
        }
        const directorDobError = validateField('directorDateOfBirth', formData.directorDateOfBirth);
        if (directorDobError) stepErrors.directorDateOfBirth = getValidationErrorMessage(directorDobError, t);
        if (!formData.icomRepresentativeTitle) stepErrors.icomRepresentativeTitle = t('register.titleRequired');
        if (!hasBilingualText(formData.icomRepresentativeFirstNameEn, formData.icomRepresentativeFirstNameGr)) {
          stepErrors.icomRepresentativeFirstName = t('register.firstNameRequired');
        }
        const icomRepLastNameErrorKey = validation.bilingualLastNameErrorKey(
          formData.icomRepresentativeLastNameEn,
          formData.icomRepresentativeLastNameGr
        );
        if (icomRepLastNameErrorKey) {
          stepErrors.icomRepresentativeLastName = t(icomRepLastNameErrorKey);
        }
        const repDobError = validateField('icomRepresentativeDateOfBirth', formData.icomRepresentativeDateOfBirth);
        if (repDobError) stepErrors.icomRepresentativeDateOfBirth = getValidationErrorMessage(repDobError, t);
        if (!formData.icomRepresentativePosition?.trim()) {
          stepErrors.icomRepresentativePosition = t('register.positionRequired');
        }
        if (!formData.icomRepresentativeEmail?.trim()) {
          stepErrors.icomRepresentativeEmail = t('validation.emailRequired');
        } else {
          const icomEmailError = validateField('icomRepresentativeEmail', formData.icomRepresentativeEmail);
          if (icomEmailError) stepErrors.icomRepresentativeEmail = getValidationErrorMessage(icomEmailError, t);
        }
        if (!formData.icomContactPreference) {
          stepErrors.icomContactPreference = t('register.contactPreferenceRequired');
        } else if (
          formData.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
          !hasSecondaryAddressContent(formData)
        ) {
          stepErrors.icomContactPreference = t('register.secondaryAddressRequiredForCorrespondence', {
            defaultValue:
              'Add a secondary address before selecting it for correspondence, or choose the primary address.',
          });
        }
        if (Object.keys(stepErrors).length > 0) stepValid = false;
      }

      if (s === 4) {
        if (committeesLoadError) {
          setError(committeesLoadError);
          stepValid = false;
        }
        stepValid = validateRepresentative('rep1', stepErrors) && stepValid;
        stepValid = validateRepresentative('rep2', stepErrors) && stepValid;
        stepValid = validateRepresentative('rep3', stepErrors) && stepValid;
        const committeeIds = [formData.rep1CommitteeId, formData.rep2CommitteeId, formData.rep3CommitteeId].filter(
          id => !!id
        );
        if (committeeIds.length > 1 && new Set(committeeIds).size !== committeeIds.length) {
          stepErrors.representativesCommittees = t('register.representativesDuplicateCommittees');
          stepValid = false;
        }
      }

      if (s === 5) {
        if (!formData.foundationAct) stepErrors.foundationAct = t('register.foundationActRequired');
        if (!formData.acceptedEthics) stepErrors.acceptedEthics = t('register.ethicsRequired');
        if (!formData.acceptedGdpr) stepErrors.acceptedGdpr = t('register.gdprRequired');
        if (!formData.acceptedDataTransfer) {
          stepErrors.acceptedDataTransfer = t('errors.api.dataTransferRequired');
        }
        if (!formData.acceptedStatement) {
          stepErrors.acceptedStatement = t('errors.api.statementRequired');
        }
        if (Object.keys(stepErrors).length > 0) {
          setError(t('register.formErrorsBelow'));
          stepValid = false;
        }
      }

      Object.assign(errors, stepErrors);
      if (!stepValid) isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const clearFieldError = (fieldName: string) => {
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const applyFieldError = (fieldName: string, message?: string) => {
    setFieldErrors(prev => {
      if (!message) {
        if (!prev[fieldName]) return prev;
        const next = { ...prev };
        delete next[fieldName];
        return next;
      }
      return { ...prev, [fieldName]: message };
    });
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));

    if (fieldName === 'institutionNameEn' || fieldName === 'institutionNameGr') {
      const hasName =
        hasBilingualText(formData.institutionNameEn, formData.institutionNameGr) ||
        !!formData.institutionName?.trim();
      const nameMsg = hasName ? undefined : t('register.institutionNameRequired');
      applyFieldError('institutionName', nameMsg);
      applyFieldError('institutionNameEn', nameMsg);
      applyFieldError('institutionNameGr', nameMsg);
      return;
    }

    const value = (formData as Record<string, unknown>)[fieldName];
    const rawError = validateField(fieldName, typeof value === 'string' ? value : String(value ?? ''));
    applyFieldError(
      fieldName,
      rawError ? getValidationErrorMessage(rawError, t) : undefined,
    );
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    clearFieldError(fieldName);
    if (fieldName === 'institutionNameEn' || fieldName === 'institutionNameGr') {
      clearFieldError('institutionName');
    }
    if (fieldName.startsWith('institutionAddress')) clearFieldError('institutionAddress');
    if (fieldName.startsWith('institutionCity')) clearFieldError('institutionCity');
    if (fieldName === 'password' && touchedFields.confirmPassword) {
      const confirmError = validation.validatePasswordConfirmation(value, formData.confirmPassword).error;
      applyFieldError(
        'confirmPassword',
        confirmError ? getValidationErrorMessage(confirmError, t) : undefined,
      );
    }
  };

  const handleNext = async () => {
    if (!validateStep(step)) {
      setError(prev => prev || t('register.formErrorsBelow'));
      requestAnimationFrame(() => focusFormError('register-form-errors'));
      return;
    }

    if (step === 1) {
      try {
        const available = await authService.isEmailAvailable(formData.email.trim());
        if (!available) {
          setFieldErrors(prev => ({
            ...prev,
            email: t('errors.api.emailExists'),
          }));
          setError(t('errors.api.emailExists'));
          requestAnimationFrame(() => focusFormError('register-form-errors'));
          return;
        }
      } catch {
        // Network failure: allow continue; server will re-check on submit.
      }
    }

    // Leaving address step: drop Secondary preference if the secondary block is empty.
    if (
      step === 2 &&
      formData.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
      !hasSecondaryAddressContent(formData)
    ) {
      setFormData(prev => ({ ...prev, icomContactPreference: '' }));
    }

    setError(null);
    setFieldErrors({});
    setHasAttemptedFinalSubmit(false);
    setStep(prev => Math.min(prev + 1, 5));
  };

  const handlePrevious = () => {
    setError(null);
    setFieldErrors({});
    setHasAttemptedFinalSubmit(false);
    setStep(prev => Math.max(prev - 1, 1));
  };

  const stepPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panel = stepPanelRef.current;
    if (!panel) return;
    panel.focus({ preventScroll: true });
    const formPanel = panel.closest('[data-auth-form-panel]') as HTMLElement | null;
    if (formPanel) {
      formPanel.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  // Never show declaration/ethics errors merely by arriving on the final step.
  useEffect(() => {
    if (step !== 5) return;
    lastStepEnteredAtRef.current = Date.now();
    setHasAttemptedFinalSubmit(false);
    setFieldErrors(prev => {
      if (!CONSENT_ERROR_KEYS.some(k => prev[k])) return prev;
      const next = { ...prev };
      CONSENT_ERROR_KEYS.forEach(k => delete next[k]);
      return next;
    });
    setError(prev => (prev && /ethics|GDPR|δεοντολογ|ιδιωτικότητας|formErrorsBelow/i.test(prev) ? null : prev));
  }, [step]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;
    const isFinalSubmitClick =
      !!submitter && submitter.getAttribute('data-action') === 'register-complete';

    // Enter / incidental submit on earlier steps: advance only (never run declaration/GDPR yet).
    if (step !== 5 || !isFinalSubmitClick) {
      if (step !== 5) {
        handleNext();
      }
      return;
    }

    // Ignore a click that was aimed at Continue and landed on Complete after the step swap.
    if (Date.now() - lastStepEnteredAtRef.current < FINAL_STEP_SUBMIT_GRACE_MS) {
      return;
    }

    setHasAttemptedFinalSubmit(true);
    if (!validateStep(5, { includeConsent: true }) || !validateAllForSubmit()) {
      return;
    }
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      if (isResubmit) {
        await applicationStatusService.resubmit(resubmitReference, {
          applicantNote: applicantNote || undefined,
          institutionNameEn: formData.institutionNameEn || undefined,
          institutionNameGr: formData.institutionNameGr || undefined,
          foundationDate: formData.foundationDate || undefined,
          website: formData.website || undefined,
          institutionType: formData.institutionType
            ? JSON.stringify([formData.institutionType])
            : undefined,
          administrativeStatus: formData.administrativeStatus,
          institutionPhone: formData.institutionPhone || undefined,
          institutionEmail: formData.institutionEmail || undefined,
          institutionAddressEn: formData.institutionAddressEn || undefined,
          institutionAddressGr: formData.institutionAddressGr || undefined,
          institutionCityEn: formData.institutionCityEn || undefined,
          institutionCityGr: formData.institutionCityGr || undefined,
          institutionPostalCodeEn: formData.institutionPostalCodeEn || undefined,
          institutionPostalCodeGr: formData.institutionPostalCodeGr || undefined,
          institutionCountryEn: formData.institutionCountryEn || undefined,
          institutionCountryGr: formData.institutionCountryGr || undefined,
          secondaryAddressEn: formData.secondaryAddressEn || undefined,
          secondaryAddressGr: formData.secondaryAddressGr || undefined,
          secondaryCityEn: formData.secondaryCityEn || undefined,
          secondaryCityGr: formData.secondaryCityGr || undefined,
          secondaryPostalCodeEn: formData.secondaryPostalCodeEn || undefined,
          secondaryPostalCodeGr: formData.secondaryPostalCodeGr || undefined,
          secondaryCountryEn: formData.secondaryCountryEn || undefined,
          secondaryCountryGr: formData.secondaryCountryGr || undefined,
          membershipCategory: formData.membershipCategory,
          directorTitle: formData.directorTitle || undefined,
          directorFirstNameEn: formData.directorFirstNameEn || undefined,
          directorFirstNameGr: formData.directorFirstNameGr || undefined,
          directorLastNameEn: formData.directorLastNameEn || undefined,
          directorLastNameGr: formData.directorLastNameGr || undefined,
          directorDateOfBirth: formData.directorDateOfBirth || undefined,
          directorEmail: formData.directorEmail || undefined,
          directorPhone: formData.directorPhone || undefined,
          icomRepresentativeTitle: formData.icomRepresentativeTitle || undefined,
          icomRepresentativeFirstNameEn: formData.icomRepresentativeFirstNameEn || undefined,
          icomRepresentativeFirstNameGr: formData.icomRepresentativeFirstNameGr || undefined,
          icomRepresentativeLastNameEn: formData.icomRepresentativeLastNameEn || undefined,
          icomRepresentativeLastNameGr: formData.icomRepresentativeLastNameGr || undefined,
          icomRepresentativeDateOfBirth: formData.icomRepresentativeDateOfBirth || undefined,
          icomRepresentativePosition: formData.icomRepresentativePosition || undefined,
          icomRepresentativeEmail: formData.icomRepresentativeEmail || undefined,
          icomRepresentativePhone: formData.icomRepresentativePhone || undefined,
          icomContactPreference: formData.icomContactPreference || undefined,
          icomRepresentativeCommunicationLanguage:
            formData.icomRepresentativeCommunicationLanguage || undefined,
          declarationDate: formData.declarationDate || undefined,
          acceptedStatement: !!formData.acceptedStatement,
          representative1Title: formData.rep1Title || undefined,
          representative1FirstNameEn: formData.rep1FirstNameEn || undefined,
          representative1FirstNameGr: formData.rep1FirstNameGr || undefined,
          representative1LastNameEn: formData.rep1LastNameEn || undefined,
          representative1LastNameGr: formData.rep1LastNameGr || undefined,
          representative1DateOfBirth: formData.rep1DateOfBirth || undefined,
          representative1PositionEn: formData.rep1PositionEn || undefined,
          representative1PositionGr: formData.rep1PositionGr || undefined,
          representative1Email: formData.rep1Email || undefined,
          representative1Phone: formData.rep1Phone || undefined,
          representative1CommitteeId: formData.rep1CommitteeId || undefined,
          representative2Title: formData.rep2Title || undefined,
          representative2FirstNameEn: formData.rep2FirstNameEn || undefined,
          representative2FirstNameGr: formData.rep2FirstNameGr || undefined,
          representative2LastNameEn: formData.rep2LastNameEn || undefined,
          representative2LastNameGr: formData.rep2LastNameGr || undefined,
          representative2DateOfBirth: formData.rep2DateOfBirth || undefined,
          representative2PositionEn: formData.rep2PositionEn || undefined,
          representative2PositionGr: formData.rep2PositionGr || undefined,
          representative2Email: formData.rep2Email || undefined,
          representative2Phone: formData.rep2Phone || undefined,
          representative2CommitteeId: formData.rep2CommitteeId || undefined,
          representative3Title: formData.rep3Title || undefined,
          representative3FirstNameEn: formData.rep3FirstNameEn || undefined,
          representative3FirstNameGr: formData.rep3FirstNameGr || undefined,
          representative3LastNameEn: formData.rep3LastNameEn || undefined,
          representative3LastNameGr: formData.rep3LastNameGr || undefined,
          representative3DateOfBirth: formData.rep3DateOfBirth || undefined,
          representative3PositionEn: formData.rep3PositionEn || undefined,
          representative3PositionGr: formData.rep3PositionGr || undefined,
          representative3Email: formData.rep3Email || undefined,
          representative3Phone: formData.rep3Phone || undefined,
          representative3CommitteeId: formData.rep3CommitteeId || undefined,
          institutionDescriptionDocument: formData.foundationAct,
        });
        showSuccess(t('applicationStatus.resubmitSuccess'));
        navigate(
          `/application-status?reference=${encodeURIComponent(resubmitReference)}`
        );
        return;
      }

      const payload = new FormData();
      payload.append('email', formData.email);
      payload.append('password', formData.password);
      payload.append('communicationLanguage', String(formData.communicationLanguage));
      if (formData.institutionNameEn) payload.append('institutionName.en', formData.institutionNameEn);
      if (formData.institutionNameGr) payload.append('institutionName.gr', formData.institutionNameGr);
      if (formData.foundationDate) payload.append('foundationDate', formData.foundationDate);
      if (formData.website) payload.append('website', formData.website);
      if (formData.institutionPhone) payload.append('institutionPhone', formData.institutionPhone);
      if (formData.institutionEmail) payload.append('institutionEmail', formData.institutionEmail);
      if (formData.institutionType) {
        payload.append('institutionType', JSON.stringify([formData.institutionType]));
      }
      payload.append('administrativeStatus', String(formData.administrativeStatus));
      if (formData.institutionAddressEn) payload.append('primaryAddress.addressEn', formData.institutionAddressEn);
      if (formData.institutionAddressGr) payload.append('primaryAddress.addressGr', formData.institutionAddressGr);
      if (formData.institutionPostalCodeEn) payload.append('primaryAddress.postalCodeEn', formData.institutionPostalCodeEn);
      if (formData.institutionPostalCodeGr) payload.append('primaryAddress.postalCodeGr', formData.institutionPostalCodeGr);
      if (formData.institutionCityEn) payload.append('primaryAddress.cityEn', formData.institutionCityEn);
      if (formData.institutionCityGr) payload.append('primaryAddress.cityGr', formData.institutionCityGr);
      if (formData.institutionCountryEn) payload.append('primaryAddress.countryEn', formData.institutionCountryEn);
      if (formData.institutionCountryGr) payload.append('primaryAddress.countryGr', formData.institutionCountryGr);
      if (formData.secondaryAddressEn) payload.append('secondaryAddress.addressEn', formData.secondaryAddressEn);
      if (formData.secondaryAddressGr) payload.append('secondaryAddress.addressGr', formData.secondaryAddressGr);
      if (formData.secondaryPostalCodeEn) payload.append('secondaryAddress.postalCodeEn', formData.secondaryPostalCodeEn);
      if (formData.secondaryPostalCodeGr) payload.append('secondaryAddress.postalCodeGr', formData.secondaryPostalCodeGr);
      if (formData.secondaryCityEn) payload.append('secondaryAddress.cityEn', formData.secondaryCityEn);
      if (formData.secondaryCityGr) payload.append('secondaryAddress.cityGr', formData.secondaryCityGr);
      if (formData.secondaryCountryEn) payload.append('secondaryAddress.countryEn', formData.secondaryCountryEn);
      if (formData.secondaryCountryGr) payload.append('secondaryAddress.countryGr', formData.secondaryCountryGr);
      payload.append('membershipCategory', String(formData.membershipCategory));
      if (formData.directorTitle) payload.append('directorTitle', formData.directorTitle);
      if (formData.directorFirstNameEn) payload.append('directorName.firstNameEn', formData.directorFirstNameEn);
      if (formData.directorFirstNameGr) payload.append('directorName.firstNameGr', formData.directorFirstNameGr);
      if (formData.directorLastNameEn) payload.append('directorName.lastNameEn', formData.directorLastNameEn);
      if (formData.directorLastNameGr) payload.append('directorName.lastNameGr', formData.directorLastNameGr);
      if (formData.directorDateOfBirth) payload.append('directorDateOfBirth', formData.directorDateOfBirth);
      if (formData.directorEmail) payload.append('directorEmail', formData.directorEmail);
      if (formData.directorPhone) payload.append('directorPhone', formData.directorPhone);
      if (formData.icomRepresentativeTitle) payload.append('icomRepresentativeTitle', formData.icomRepresentativeTitle);
      if (formData.icomRepresentativeFirstNameEn) payload.append('icomRepresentativeName.firstNameEn', formData.icomRepresentativeFirstNameEn);
      if (formData.icomRepresentativeFirstNameGr) payload.append('icomRepresentativeName.firstNameGr', formData.icomRepresentativeFirstNameGr);
      if (formData.icomRepresentativeLastNameEn) payload.append('icomRepresentativeName.lastNameEn', formData.icomRepresentativeLastNameEn);
      if (formData.icomRepresentativeLastNameGr) payload.append('icomRepresentativeName.lastNameGr', formData.icomRepresentativeLastNameGr);
      if (formData.icomRepresentativeDateOfBirth) payload.append('icomRepresentativeDateOfBirth', formData.icomRepresentativeDateOfBirth);
      if (formData.icomRepresentativePosition) payload.append('icomRepresentativePosition', formData.icomRepresentativePosition);
      if (formData.icomRepresentativeEmail) payload.append('icomRepresentativeEmail', formData.icomRepresentativeEmail);
      if (formData.icomRepresentativePhone) payload.append('icomRepresentativePhone', formData.icomRepresentativePhone);
      if (formData.icomContactPreference) payload.append('icomContactPreference', formData.icomContactPreference);
      if (formData.icomRepresentativeCommunicationLanguage) payload.append('icomRepresentativeCommunicationLanguage', String(formData.icomRepresentativeCommunicationLanguage));
      if (formData.declarationDate) payload.append('declarationDate', formData.declarationDate);
      if (formData.rep1Title) payload.append('representative1Title', formData.rep1Title);
      if (formData.rep1DateOfBirth) payload.append('representative1DateOfBirth', formData.rep1DateOfBirth);
      if (formData.rep1PositionEn) payload.append('representative1PositionEn', formData.rep1PositionEn);
      if (formData.rep1PositionGr) payload.append('representative1PositionGr', formData.rep1PositionGr);
      if (formData.rep1FirstNameEn) payload.append('representative1Name.firstNameEn', formData.rep1FirstNameEn);
      if (formData.rep1FirstNameGr) payload.append('representative1Name.firstNameGr', formData.rep1FirstNameGr);
      if (formData.rep1LastNameEn) payload.append('representative1Name.lastNameEn', formData.rep1LastNameEn);
      if (formData.rep1LastNameGr) payload.append('representative1Name.lastNameGr', formData.rep1LastNameGr);
      if (formData.rep1Email) payload.append('representative1Email', formData.rep1Email);
      if (formData.rep1Phone) payload.append('representative1Phone', formData.rep1Phone);
      if (formData.rep1CommitteeId) payload.append('representative1CommitteeId', formData.rep1CommitteeId);
      if (formData.rep2Title) payload.append('representative2Title', formData.rep2Title);
      if (formData.rep2DateOfBirth) payload.append('representative2DateOfBirth', formData.rep2DateOfBirth);
      if (formData.rep2PositionEn) payload.append('representative2PositionEn', formData.rep2PositionEn);
      if (formData.rep2PositionGr) payload.append('representative2PositionGr', formData.rep2PositionGr);
      if (formData.rep2FirstNameEn) payload.append('representative2Name.firstNameEn', formData.rep2FirstNameEn);
      if (formData.rep2FirstNameGr) payload.append('representative2Name.firstNameGr', formData.rep2FirstNameGr);
      if (formData.rep2LastNameEn) payload.append('representative2Name.lastNameEn', formData.rep2LastNameEn);
      if (formData.rep2LastNameGr) payload.append('representative2Name.lastNameGr', formData.rep2LastNameGr);
      if (formData.rep2Email) payload.append('representative2Email', formData.rep2Email);
      if (formData.rep2Phone) payload.append('representative2Phone', formData.rep2Phone);
      if (formData.rep2CommitteeId) payload.append('representative2CommitteeId', formData.rep2CommitteeId);
      if (formData.rep3Title) payload.append('representative3Title', formData.rep3Title);
      if (formData.rep3DateOfBirth) payload.append('representative3DateOfBirth', formData.rep3DateOfBirth);
      if (formData.rep3PositionEn) payload.append('representative3PositionEn', formData.rep3PositionEn);
      if (formData.rep3PositionGr) payload.append('representative3PositionGr', formData.rep3PositionGr);
      if (formData.rep3FirstNameEn) payload.append('representative3Name.firstNameEn', formData.rep3FirstNameEn);
      if (formData.rep3FirstNameGr) payload.append('representative3Name.firstNameGr', formData.rep3FirstNameGr);
      if (formData.rep3LastNameEn) payload.append('representative3Name.lastNameEn', formData.rep3LastNameEn);
      if (formData.rep3LastNameGr) payload.append('representative3Name.lastNameGr', formData.rep3LastNameGr);
      if (formData.rep3Email) payload.append('representative3Email', formData.rep3Email);
      if (formData.rep3Phone) payload.append('representative3Phone', formData.rep3Phone);
      if (formData.rep3CommitteeId) payload.append('representative3CommitteeId', formData.rep3CommitteeId);
      payload.append('acceptedEthics', String(formData.acceptedEthics));
      payload.append('acceptedGdpr', String(formData.acceptedGdpr));
      payload.append('acceptedDataTransfer', String(formData.acceptedDataTransfer));
      payload.append('acceptedStatement', String(formData.acceptedStatement));
      if (formData.foundationAct) {
        payload.append('institutionDescriptionDocument', formData.foundationAct);
      }

      if (isAdminCreate) {
        const created = await adminService.createInstitution(payload);
        showSuccess(t('register.registrationSuccessful'));
        navigate(`/admin/institutions/${created.id}`);
        return;
      }

      const result = await authService.registerInstitutionComplete(payload);
      try {
        localStorage.removeItem('icom-register-institution-draft');
      } catch {
        // ignore
      }
      showSuccess(t('register.registrationSuccessful'));
      navigate('/register-success', {
        state: {
          email: formData.email,
          message: t('register.checkEmailConfirmation'),
          applicationReferenceNumber: result.applicationReferenceNumber,
        },
      });
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : '';
      setError(getApiErrorMessage(rawMessage, t, { registration: true }));
    } finally {
      setLoading(false);
    }
  };

  const uniqueCommitteeList = uniqueInternationalCommittees(committees, [
    formData.rep1CommitteeId,
    formData.rep2CommitteeId,
    formData.rep3CommitteeId,
  ]);

  const steps = [
    { number: 1, label: t('register.institution.step1') },
    { number: 2, label: t('register.institution.step2') },
    { number: 3, label: t('register.institution.step3') },
    { number: 4, label: t('register.institution.step4') },
    { number: 5, label: t('register.institution.step5') },
  ];

  const isLastStep = step === 5;
  const navActions = (
    <>
      {step > 1 ? (
        <Button type="button" variant="outline" onClick={handlePrevious}>
          {t('common.previous')}
        </Button>
      ) : (
        <NavSpacer />
      )}
      <ButtonGroupRight>
        {!isLastStep ? (
          <Button type="button" onClick={handleNext} variant="primary">
            {t('register.continue')} →
          </Button>
        ) : (
          <Button
            type="submit"
            form="register-institution-form"
            data-action="register-complete"
            disabled={loading}
            loading={loading}
            variant="gradient"
          >
            {t('register.completeRegistration')}
          </Button>
        )}
      </ButtonGroupRight>
    </>
  );

  if (modeLoading) {
    return (
      <AuthWizardShell
        title={isResubmit ? t('register.resubmitApplication') : t('register.adminCreatingInstitution')}
        subtitle={t('common.loading')}
        titleId="register-institution-title"
      >
        <Text size="sm" color="secondary">{t('common.loading')}</Text>
      </AuthWizardShell>
    );
  }

  return (
    <AuthWizardShell
      title={
        isAdminCreate
          ? t('register.adminCreatingInstitution')
          : isResubmit
            ? t('register.resubmitApplication')
            : t('register.petitionNewOrganization')
      }
      subtitle={
        isAdminCreate
          ? t('register.adminCreatingInstitutionSubtitle')
          : isResubmit
            ? t('register.resubmitApplicationSubtitle')
            : t('register.institutionSubtitle')
      }
      titleId="register-institution-title"
      backSlot={
        <BackLink
          to={
            isAdminCreate
              ? '/admin/institutions'
              : isResubmit
                ? `/application-status?reference=${encodeURIComponent(resubmitReference || referenceParam)}`
                : '/register'
          }
        >
          ← {t('common.back')}
        </BackLink>
      }
      footerSlot={navActions}
    >
      <WizardProgress steps={steps} currentStep={step}>
        {error && (hasAttemptedFinalSubmit || step !== 5) && (
          <FormAlert variant="error" id="register-form-errors">
            {error}
          </FormAlert>
        )}

        <Form id="register-institution-form" onSubmit={handleSubmit} noValidate>
          <WizardStepPanel
            stepKey={step}
            aria-label={steps.find(s => s.number === step)?.label}
          >
          <StepPanel
            ref={stepPanelRef}
            tabIndex={-1}
          >
            {step === 1 && (
              <FormSection
                title={t('register.institution.step1')}
                spacing="lg"
              >
                {!skipPassword && (
                  <>
                    <SectionHeading>{t('register.accountSection')}</SectionHeading>
                    <FormField
                      label={t('common.email')}
                      type="email"
                      name="email"
                      value={formData.email}
                      required
                      error={fieldErrors.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      onBlur={() => handleFieldBlur('email')}
                      autoComplete="email"
                    />
                    <FormRow>
                      <FormField
                        id="register-institution-password"
                        label={t('register.password')}
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        required
                        error={fieldErrors.password}
                        helperText={t('register.passwordHint')}
                        onChange={(e) => handleFieldChange('password', e.target.value)}
                        onBlur={() => handleFieldBlur('password')}
                        autoComplete="new-password"
                        iconRight={
                          <PasswordVisibilityToggle
                            visible={showPassword}
                            onToggle={() => setShowPassword(v => !v)}
                            controlsId="register-institution-password"
                          />
                        }
                      />
                      <FormField
                        id="register-institution-confirm-password"
                        label={t('register.confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        required
                        error={fieldErrors.confirmPassword}
                        onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                        onBlur={() => handleFieldBlur('confirmPassword')}
                        autoComplete="new-password"
                        iconRight={
                          <PasswordVisibilityToggle
                            visible={showConfirmPassword}
                            onToggle={() => setShowConfirmPassword(v => !v)}
                            controlsId="register-institution-confirm-password"
                          />
                        }
                      />
                    </FormRow>
                    <FormField
                      label={t('register.preferredLanguage')}
                      select
                      name="communicationLanguage"
                      options={[
                        { value: CommunicationLanguage.English, label: t('register.languageEnglish') },
                        { value: CommunicationLanguage.Greek, label: t('register.languageGreek') },
                        { value: CommunicationLanguage.French, label: t('register.languageFrench') },
                      ]}
                      value={formData.communicationLanguage}
                      onChange={(e) => setFormData(prev => ({ ...prev, communicationLanguage: e.target.value as CommunicationLanguage }))}
                    />
                  </>
                )}
                <SectionHeading>{t('register.institutionBasicsSection')}</SectionHeading>
                {fieldErrors.institutionName && (
                  <FormAlert variant="error">{fieldErrors.institutionName}</FormAlert>
                )}
                <FormField
                  label={t('register.institutionNameEn')}
                  name="institutionNameEn"
                  value={formData.institutionNameEn}
                  error={fieldErrors.institutionNameEn}
                  onChange={(e) => handleFieldChange('institutionNameEn', e.target.value)}
                  onBlur={() => handleFieldBlur('institutionNameEn')}
                  autoComplete="organization"
                  required
                />
                <FormField
                  label={t('register.institutionNameGr')}
                  name="institutionNameGr"
                  value={formData.institutionNameGr}
                  error={fieldErrors.institutionNameGr}
                  onChange={(e) => handleFieldChange('institutionNameGr', e.target.value)}
                  onBlur={() => handleFieldBlur('institutionNameGr')}
                  required
                />
                <FormRow>
                  <FormField
                    label={t('register.foundationDate')}
                    type="date"
                    name="foundationDate"
                    value={formData.foundationDate}
                    error={fieldErrors.foundationDate}
                    onChange={(e) => handleFieldChange('foundationDate', e.target.value)}
                    onBlur={() => handleFieldBlur('foundationDate')}
                  />
                  <FormField
                    label={t('register.website')}
                    type="url"
                    name="website"
                    value={formData.website}
                    error={fieldErrors.website}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    onBlur={() => handleFieldBlur('website')}
                    autoComplete="url"
                  />
                </FormRow>
                <FormField
                  label={t('register.administrativeStatus')}
                  select
                  name="administrativeStatus"
                  options={[
                    { value: String(AdministrativeStatus.Public), label: t('institution.administrativeStatus.public') },
                    { value: String(AdministrativeStatus.Private), label: t('institution.administrativeStatus.private') },
                    { value: String(AdministrativeStatus.Mixed), label: t('institution.administrativeStatus.mixed') },
                  ]}
                  value={String(formData.administrativeStatus)}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      administrativeStatus: parseInt(e.target.value, 10) as AdministrativeStatus,
                    }))
                  }
                />
                <IcomTypeFieldset>
                  <IcomTypeLegend>{t('institution.icomType.label')}</IcomTypeLegend>
                  <IcomTypeIntro color="secondary" size="sm">
                    {t('institution.icomType.intro')}
                  </IcomTypeIntro>
                  {ICOM_INSTITUTION_TYPE_KEYS.map(key => (
                    <IcomTypeOption key={key} htmlFor={`institution-type-${key}`}>
                      <IcomTypeRadio
                        id={`institution-type-${key}`}
                        type="radio"
                        name="institutionType"
                        value={key}
                        checked={formData.institutionType === key}
                        onChange={() => {
                          setFormData(prev => ({ ...prev, institutionType: key }));
                          clearFieldError('institutionType');
                        }}
                      />
                      <span>{t(`institution.icomType.options.${key}`)}</span>
                    </IcomTypeOption>
                  ))}
                  {fieldErrors.institutionType && (
                    <FieldErrorText role="alert">{fieldErrors.institutionType}</FieldErrorText>
                  )}
                </IcomTypeFieldset>
              </FormSection>
            )}
            {step === 2 && (
              <FormSection
                title={t('register.institution.step2')}
                spacing="lg"
              >
              <SectionHeading>{t('register.institution.primaryContact')}</SectionHeading>
              <BilingualDoubleInput
                label={t('register.institutionAddress')}
                valueEn={formData.institutionAddressEn}
                valueGr={formData.institutionAddressGr}
                onChangeEn={(v) => handleFieldChange('institutionAddressEn', v)}
                onChangeGr={(v) => handleFieldChange('institutionAddressGr', v)}
                error={fieldErrors.institutionAddress}
                required
              />
              <FormRowCityPostal>
                <BilingualDoubleInput
                  label={t('register.institutionCity')}
                  valueEn={formData.institutionCityEn}
                  valueGr={formData.institutionCityGr}
                  onChangeEn={(v) => handleFieldChange('institutionCityEn', v)}
                  onChangeGr={(v) => handleFieldChange('institutionCityGr', v)}
                  error={fieldErrors.institutionCity}
                  required
                />
                <FormField
                  label={t('register.institutionPostalCode')}
                  name="institutionPostalCode"
                  value={formData.institutionPostalCodeEn || formData.institutionPostalCodeGr}
                  alignWithBilingual
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({ ...prev, institutionPostalCodeEn: v, institutionPostalCodeGr: v }));
                  }}
                />
              </FormRowCityPostal>
              <CountrySelector
                label={t('register.institutionCountry')}
                valueEn={formData.institutionCountryEn}
                valueGr={formData.institutionCountryGr}
                onChange={(en, gr) => {
                  setFormData(prev => ({ ...prev, institutionCountryEn: en, institutionCountryGr: gr }));
                  clearFieldError('institutionCountry');
                }}
                placeholder={t('register.selectCountry')}
                error={fieldErrors.institutionCountry}
                required
              />
              <FormRow>
                <PhoneWithDialCode
                  id="institution-phone"
                  name="institutionPhone"
                  label={t('register.individual.telephoneWithCountryCode')}
                  value={formData.institutionPhone}
                  error={fieldErrors.institutionPhone}
                  onChange={(phone) => handleFieldChange('institutionPhone', phone)}
                  onBlur={() => handleFieldBlur('institutionPhone')}
                  autoComplete="tel"
                  required
                />
                <FormField
                  label={t('register.institutionEmail')}
                  type="email"
                  name="institutionEmail"
                  value={formData.institutionEmail}
                  error={fieldErrors.institutionEmail}
                  onChange={(e) => handleFieldChange('institutionEmail', e.target.value)}
                  onBlur={() => handleFieldBlur('institutionEmail')}
                  autoComplete="email"
                  required
                />
              </FormRow>
              <SectionHeading>{t('register.institution.secondaryContactHeading')}</SectionHeading>
              <BilingualDoubleInput
                label={t('register.address')}
                valueEn={formData.secondaryAddressEn}
                valueGr={formData.secondaryAddressGr}
                onChangeEn={(v) =>
                  setFormData(prev => {
                    const next = { ...prev, secondaryAddressEn: v };
                    if (
                      next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                      !hasSecondaryAddressContent(next)
                    ) {
                      next.icomContactPreference = '';
                    }
                    return next;
                  })
                }
                onChangeGr={(v) =>
                  setFormData(prev => {
                    const next = { ...prev, secondaryAddressGr: v };
                    if (
                      next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                      !hasSecondaryAddressContent(next)
                    ) {
                      next.icomContactPreference = '';
                    }
                    return next;
                  })
                }
              />
              <FormRowCityPostal>
                <BilingualDoubleInput
                  label={t('register.institutionCity')}
                  valueEn={formData.secondaryCityEn}
                  valueGr={formData.secondaryCityGr}
                  onChangeEn={(v) =>
                    setFormData(prev => {
                      const next = { ...prev, secondaryCityEn: v };
                      if (
                        next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                        !hasSecondaryAddressContent(next)
                      ) {
                        next.icomContactPreference = '';
                      }
                      return next;
                    })
                  }
                  onChangeGr={(v) =>
                    setFormData(prev => {
                      const next = { ...prev, secondaryCityGr: v };
                      if (
                        next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                        !hasSecondaryAddressContent(next)
                      ) {
                        next.icomContactPreference = '';
                      }
                      return next;
                    })
                  }
                />
                <FormField
                  label={t('register.institutionPostalCode')}
                  value={formData.secondaryPostalCodeEn || formData.secondaryPostalCodeGr}
                  alignWithBilingual
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => {
                      const next = {
                        ...prev,
                        secondaryPostalCodeEn: v,
                        secondaryPostalCodeGr: v,
                      };
                      if (
                        next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                        !hasSecondaryAddressContent(next)
                      ) {
                        next.icomContactPreference = '';
                      }
                      return next;
                    });
                  }}
                />
              </FormRowCityPostal>
              <CountrySelector
                label={t('register.institutionCountry')}
                valueEn={formData.secondaryCountryEn}
                valueGr={formData.secondaryCountryGr}
                onChange={(en, gr) =>
                  setFormData(prev => {
                    const next = { ...prev, secondaryCountryEn: en, secondaryCountryGr: gr };
                    if (
                      next.icomContactPreference === String(InstitutionContactPreference.Secondary) &&
                      !hasSecondaryAddressContent(next)
                    ) {
                      next.icomContactPreference = '';
                    }
                    return next;
                  })
                }
                placeholder={t('register.selectCountry')}
              />
              </FormSection>
            )}
            {step === 3 && (
              <FormSection
                title={t('register.institution.step3')}
                spacing="lg"
              >
              <SectionHeading>{t('register.institutionDirector')}</SectionHeading>
              <FormRow>
                <FormField
                  label={t('register.titleOption')}
                  select
                  required
                  options={[
                    { value: '', label: t('register.titlePlaceholder') },
                    { value: String(Title.Mr), label: t('register.titleMr') },
                    { value: String(Title.Ms), label: t('register.titleMs') },
                    { value: String(Title.Mrs), label: t('register.titleMrs') },
                    { value: String(Title.Dr), label: t('register.titleDr') },
                    { value: String(Title.Prof), label: t('register.titleProf') },
                    { value: String(Title.Other), label: t('register.titleOther') },
                  ]}
                  name="directorTitle"
                  value={formData.directorTitle}
                  error={fieldErrors.directorTitle}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, directorTitle: e.target.value }));
                    clearFieldError('directorTitle');
                  }}
                />
                <FormField
                  label={t('register.dateOfBirthShort')}
                  labelSize="sm"
                  type="date"
                  name="directorDateOfBirth"
                  value={formData.directorDateOfBirth}
                  error={fieldErrors.directorDateOfBirth}
                  onChange={(e) => handleFieldChange('directorDateOfBirth', e.target.value)}
                  onBlur={() => handleFieldBlur('directorDateOfBirth')}
                />
              </FormRow>
              <FormRow>
                <BilingualDoubleInput
                  label={t('common.lastName')}
                  valueEn={formData.directorLastNameEn}
                  valueGr={formData.directorLastNameGr}
                  onChangeEn={(v) => {
                    setFormData(prev => ({ ...prev, directorLastNameEn: v }));
                    clearFieldError('directorLastName');
                  }}
                  onChangeGr={(v) => {
                    setFormData(prev => ({ ...prev, directorLastNameGr: v }));
                    clearFieldError('directorLastName');
                  }}
                  error={fieldErrors.directorLastName}
                  required
                />
                <BilingualDoubleInput
                  label={t('common.firstName')}
                  valueEn={formData.directorFirstNameEn}
                  valueGr={formData.directorFirstNameGr}
                  onChangeEn={(v) => {
                    setFormData(prev => ({ ...prev, directorFirstNameEn: v }));
                    clearFieldError('directorFirstName');
                  }}
                  onChangeGr={(v) => {
                    setFormData(prev => ({ ...prev, directorFirstNameGr: v }));
                    clearFieldError('directorFirstName');
                  }}
                  error={fieldErrors.directorFirstName}
                  required
                />
              </FormRow>
              <FormRow>
                <FormField
                  label={t('register.directorEmail')}
                  type="email"
                  name="directorEmail"
                  value={formData.directorEmail}
                  error={fieldErrors.directorEmail}
                  onChange={(e) => handleFieldChange('directorEmail', e.target.value)}
                  onBlur={() => handleFieldBlur('directorEmail')}
                />
                <PhoneWithDialCode
                  id="director-phone"
                  name="directorPhone"
                  label={t('register.individual.telephoneWithCountryCode')}
                  value={formData.directorPhone}
                  error={fieldErrors.directorPhone}
                  onChange={(phone) => handleFieldChange('directorPhone', phone)}
                  onBlur={() => handleFieldBlur('directorPhone')}
                  autoComplete="tel"
                />
              </FormRow>
              <SectionHeading>{t('register.icomContactPerson')}</SectionHeading>
              <FormRow>
                <FormField
                  label={t('register.titleOption')}
                  select
                  required
                  options={[
                    { value: '', label: t('register.titlePlaceholder') },
                    { value: String(Title.Mr), label: t('register.titleMr') },
                    { value: String(Title.Ms), label: t('register.titleMs') },
                    { value: String(Title.Mrs), label: t('register.titleMrs') },
                    { value: String(Title.Dr), label: t('register.titleDr') },
                    { value: String(Title.Prof), label: t('register.titleProf') },
                    { value: String(Title.Other), label: t('register.titleOther') },
                  ]}
                  name="icomRepresentativeTitle"
                  value={formData.icomRepresentativeTitle}
                  error={fieldErrors.icomRepresentativeTitle}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, icomRepresentativeTitle: e.target.value }));
                    clearFieldError('icomRepresentativeTitle');
                  }}
                />
                <FormField
                  label={t('register.dateOfBirthShort')}
                  labelSize="sm"
                  type="date"
                  name="icomRepresentativeDateOfBirth"
                  value={formData.icomRepresentativeDateOfBirth}
                  error={fieldErrors.icomRepresentativeDateOfBirth}
                  onChange={(e) => handleFieldChange('icomRepresentativeDateOfBirth', e.target.value)}
                  onBlur={() => handleFieldBlur('icomRepresentativeDateOfBirth')}
                />
              </FormRow>
              <FormRow>
                <BilingualDoubleInput
                  label={t('common.lastName')}
                  valueEn={formData.icomRepresentativeLastNameEn}
                  valueGr={formData.icomRepresentativeLastNameGr}
                  onChangeEn={(v) => {
                    setFormData(prev => ({ ...prev, icomRepresentativeLastNameEn: v }));
                    clearFieldError('icomRepresentativeLastName');
                  }}
                  onChangeGr={(v) => {
                    setFormData(prev => ({ ...prev, icomRepresentativeLastNameGr: v }));
                    clearFieldError('icomRepresentativeLastName');
                  }}
                  error={fieldErrors.icomRepresentativeLastName}
                  required
                />
                <BilingualDoubleInput
                  label={t('common.firstName')}
                  valueEn={formData.icomRepresentativeFirstNameEn}
                  valueGr={formData.icomRepresentativeFirstNameGr}
                  onChangeEn={(v) => {
                    setFormData(prev => ({ ...prev, icomRepresentativeFirstNameEn: v }));
                    clearFieldError('icomRepresentativeFirstName');
                  }}
                  onChangeGr={(v) => {
                    setFormData(prev => ({ ...prev, icomRepresentativeFirstNameGr: v }));
                    clearFieldError('icomRepresentativeFirstName');
                  }}
                  error={fieldErrors.icomRepresentativeFirstName}
                  required
                />
              </FormRow>
              <FormField
                label={t('register.icomRepresentativePosition')}
                name="icomRepresentativePosition"
                value={formData.icomRepresentativePosition}
                error={fieldErrors.icomRepresentativePosition}
                onChange={(e) => handleFieldChange('icomRepresentativePosition', e.target.value)}
                required
              />
              <FormRow>
                <FormField
                  label={t('register.icomRepresentativeEmail')}
                  type="email"
                  name="icomRepresentativeEmail"
                  value={formData.icomRepresentativeEmail}
                  error={fieldErrors.icomRepresentativeEmail}
                  onChange={(e) => handleFieldChange('icomRepresentativeEmail', e.target.value)}
                  onBlur={() => handleFieldBlur('icomRepresentativeEmail')}
                  autoComplete="email"
                  required
                />
                <PhoneWithDialCode
                  id="icom-representative-phone"
                  name="icomRepresentativePhone"
                  label={t('register.individual.telephoneWithCountryCode')}
                  value={formData.icomRepresentativePhone}
                  onChange={(phone) => handleFieldChange('icomRepresentativePhone', phone)}
                  autoComplete="tel"
                />
              </FormRow>
              <FormField
                label={t('register.useInstitutionContact')}
                select
                name="icomContactPreference"
                required
                helperText={
                  hasSecondaryAddressContent(formData)
                    ? t('register.icomContactPreferenceHint')
                    : t('register.icomContactPreferenceSecondaryUnavailable', {
                        defaultValue:
                          'Secondary address is empty, so correspondence can only use the primary institution address. Add a secondary address on the previous step to enable it.',
                      })
                }
                options={[
                  { value: '', label: t('register.selectContactPreference') },
                  {
                    value: String(InstitutionContactPreference.Primary),
                    label: t('register.contactPreferencePrimary'),
                  },
                  ...(hasSecondaryAddressContent(formData)
                    ? [
                        {
                          value: String(InstitutionContactPreference.Secondary),
                          label: t('register.contactPreferenceSecondary'),
                        },
                      ]
                    : []),
                ]}
                value={formData.icomContactPreference}
                error={fieldErrors.icomContactPreference}
                onChange={(e) => {
                  const pref = e.target.value;
                  if (
                    pref === String(InstitutionContactPreference.Secondary) &&
                    !hasSecondaryAddressContent(formData)
                  ) {
                    setFieldErrors(prev => ({
                      ...prev,
                      icomContactPreference: t('register.secondaryAddressRequiredForCorrespondence', {
                        defaultValue:
                          'Add a secondary address before selecting it for correspondence, or choose the primary address.',
                      }),
                    }));
                    return;
                  }
                  setFormData(prev => {
                    const next = { ...prev, icomContactPreference: pref };
                    // Primary = institution main contact: prefill empty person email/phone from institution.
                    if (pref === String(InstitutionContactPreference.Primary)) {
                      if (!prev.icomRepresentativeEmail?.trim() && prev.institutionEmail?.trim()) {
                        next.icomRepresentativeEmail = prev.institutionEmail;
                      }
                      if (!prev.icomRepresentativePhone?.trim() && prev.institutionPhone?.trim()) {
                        next.icomRepresentativePhone = prev.institutionPhone;
                      }
                    }
                    return next;
                  });
                  clearFieldError('icomContactPreference');
                  clearFieldError('icomRepresentativeEmail');
                }}
              />
              <FormField
                label={t('register.languageForCommunication')}
                select
                options={[
                  { value: CommunicationLanguage.English, label: t('register.languageEnglish') },
                  { value: CommunicationLanguage.Greek, label: t('register.languageGreek') },
                  { value: CommunicationLanguage.French, label: t('register.languageFrench') },
                ]}
                value={formData.icomRepresentativeCommunicationLanguage}
                onChange={(e) => setFormData(prev => ({ ...prev, icomRepresentativeCommunicationLanguage: e.target.value as CommunicationLanguage }))}
              />
              </FormSection>
            )}
            {step === 4 && (
              <FormSection
                title={t('register.institution.step4')}
                spacing="lg"
              >
              {committeesLoadError && (
                <SpacedBlock style={{ marginBottom: spacing[4] }}>
                  <Text size="sm" color="error" style={{ marginBottom: spacing[2] }}>{committeesLoadError}</Text>
                  <Button type="button" variant="outline" size="sm" onClick={() => loadCommittees()}>
                    {t('register.committeesRetry')}
                  </Button>
                </SpacedBlock>
              )}
              <FormField
                label={t('register.budgetCategory')}
                select
                options={[
                  { value: InstitutionMembershipCategory.ActiveI.toString(), label: t('register.budgetLessThan30k') },
                  { value: InstitutionMembershipCategory.ActiveII.toString(), label: t('register.budget30kTo100k') },
                  { value: InstitutionMembershipCategory.ActiveIII.toString(), label: t('register.budget100kTo1M') },
                  { value: InstitutionMembershipCategory.ActiveIV.toString(), label: t('register.budget1MTo5M') },
                  { value: InstitutionMembershipCategory.ActiveV.toString(), label: t('register.budget5MTo10M') },
                  { value: InstitutionMembershipCategory.ActiveVI.toString(), label: t('register.budgetMoreThan10M') },
                  { value: InstitutionMembershipCategory.Benefactor.toString(), label: t('register.budgetBenefactor') },
                ]}
                value={formData.membershipCategory.toString()}
                onChange={(e) => setFormData(prev => ({ ...prev, membershipCategory: parseInt(e.target.value) as InstitutionMembershipCategory }))}
                helperText={t('register.memberCardsFromCategory', {
                  count: memberCardsForCategory(formData.membershipCategory),
                })}
              />
              <RepresentativesBlock>
                <RepresentativesBlockTitle>{t('register.designatedRepresentatives')}</RepresentativesBlockTitle>
                <Text size="sm" color="secondary" style={{ marginBottom: spacing[4] }}>
                  {t('register.institution.designatedRepresentativesHint')}
                </Text>
                <Button variant="outline" type="button" size="sm" onClick={() => setCommitteesModalOpen(true)} style={{ marginBottom: spacing[6] }}>
                  {t('register.aboutCommittees')}
                </Button>
                {fieldErrors.representativesCommittees && (
                  <FormAlert variant="error">{fieldErrors.representativesCommittees}</FormAlert>
                )}
                {(['rep1', 'rep2', 'rep3'] as const).map((prefix, index) => {
                  const titleKey = `${prefix}Title` as const;
                  const dobKey = `${prefix}DateOfBirth` as const;
                  const lastEnKey = `${prefix}LastNameEn` as const;
                  const lastGrKey = `${prefix}LastNameGr` as const;
                  const firstEnKey = `${prefix}FirstNameEn` as const;
                  const firstGrKey = `${prefix}FirstNameGr` as const;
                  const positionEnKey = `${prefix}PositionEn` as const;
                  const positionGrKey = `${prefix}PositionGr` as const;
                  const emailKey = `${prefix}Email` as const;
                  const phoneKey = `${prefix}Phone` as const;
                  const committeeKey = `${prefix}CommitteeId` as const;
                  const titleLabels = [
                    t('register.representative1'),
                    t('register.representative2'),
                    t('register.representative3'),
                  ];
                  const setRep = (key: string, value: string) =>
                    setFormData(prev => ({ ...prev, [key]: value }));
                  const repRequired = isRepresentativeStarted(prefix);
                  return (
                    <RepCard variant="elevated" key={prefix}>
                      <RepCardTitle>{titleLabels[index]}</RepCardTitle>
                      <FormRow>
                        <FormField
                          label={t('register.titleOption')}
                          select
                          required={repRequired}
                          options={[
                            { value: '', label: t('register.titlePlaceholder') },
                            { value: String(Title.Mr), label: t('register.titleMr') },
                            { value: String(Title.Ms), label: t('register.titleMs') },
                            { value: String(Title.Mrs), label: t('register.titleMrs') },
                            { value: String(Title.Dr), label: t('register.titleDr') },
                            { value: String(Title.Prof), label: t('register.titleProf') },
                            { value: String(Title.Other), label: t('register.titleOther') },
                          ]}
                          value={(formData as any)[titleKey]}
                          error={fieldErrors[titleKey]}
                          onChange={(e) => {
                            setRep(titleKey, e.target.value);
                            clearFieldError(titleKey);
                          }}
                        />
                        <FormField
                          label={t('register.dateOfBirthShort')}
                          labelSize="sm"
                          type="date"
                          required={repRequired}
                          value={(formData as any)[dobKey]}
                          error={fieldErrors[dobKey]}
                          onChange={(e) => {
                            setRep(dobKey, e.target.value);
                            clearFieldError(dobKey);
                          }}
                        />
                      </FormRow>
                      <FormRow>
                        <BilingualDoubleInput
                          label={t('common.lastName')}
                          required={repRequired}
                          valueEn={(formData as any)[lastEnKey]}
                          valueGr={(formData as any)[lastGrKey]}
                          onChangeEn={(v) => {
                            setRep(lastEnKey, v);
                            clearFieldError(`${prefix}LastName`);
                          }}
                          onChangeGr={(v) => {
                            setRep(lastGrKey, v);
                            clearFieldError(`${prefix}LastName`);
                          }}
                          error={fieldErrors[`${prefix}LastName`]}
                        />
                        <BilingualDoubleInput
                          label={t('common.firstName')}
                          required={repRequired}
                          valueEn={(formData as any)[firstEnKey]}
                          valueGr={(formData as any)[firstGrKey]}
                          onChangeEn={(v) => {
                            setRep(firstEnKey, v);
                            clearFieldError(`${prefix}FirstName`);
                          }}
                          onChangeGr={(v) => {
                            setRep(firstGrKey, v);
                            clearFieldError(`${prefix}FirstName`);
                          }}
                          error={fieldErrors[`${prefix}FirstName`]}
                        />
                      </FormRow>
                      <BilingualDoubleInput
                        label={t('register.icomRepresentativePosition')}
                        required={repRequired}
                        valueEn={(formData as any)[positionEnKey]}
                        valueGr={(formData as any)[positionGrKey]}
                        onChangeEn={(v) => {
                          setRep(positionEnKey, v);
                          clearFieldError(`${prefix}Position`);
                        }}
                        onChangeGr={(v) => {
                          setRep(positionGrKey, v);
                          clearFieldError(`${prefix}Position`);
                        }}
                        error={fieldErrors[`${prefix}Position`]}
                      />
                      <FormRow>
                        <FormField
                          label={t('register.icomRepresentativeEmail')}
                          type="email"
                          required={repRequired}
                          value={(formData as any)[emailKey]}
                          error={fieldErrors[emailKey]}
                          onChange={(e) => {
                            setRep(emailKey, e.target.value);
                            clearFieldError(emailKey);
                          }}
                          autoComplete="email"
                        />
                        <PhoneWithDialCode
                          id={`${prefix}-phone`}
                          name={phoneKey}
                          label={t('register.individual.telephoneWithCountryCode')}
                          required={repRequired}
                          value={(formData as any)[phoneKey]}
                          error={fieldErrors[phoneKey]}
                          onChange={(phone) => {
                            setRep(phoneKey, phone);
                            clearFieldError(phoneKey);
                          }}
                          autoComplete="tel"
                        />
                      </FormRow>
                      <FormField
                        label={t('register.internationalCommittee')}
                        select
                        required={repRequired}
                        options={[
                          { value: '', label: t('register.committees.searchPlaceholder') },
                          ...uniqueCommitteeList
                            .filter(c => {
                              const currentId = (formData as any)[committeeKey] as string;
                              if (currentId && c.id === currentId) return true;
                              const takenByOthers = [
                                prefix === 'rep1' ? '' : formData.rep1CommitteeId,
                                prefix === 'rep2' ? '' : formData.rep2CommitteeId,
                                prefix === 'rep3' ? '' : formData.rep3CommitteeId,
                              ].filter(Boolean);
                              return !takenByOthers.includes(c.id);
                            })
                            .map(c => ({
                              value: c.id,
                              label: i18n.language === 'gr' ? (c.nameGr || c.nameEn) : (c.nameEn || c.nameGr),
                            })),
                        ]}
                        value={(formData as any)[committeeKey]}
                        error={fieldErrors[committeeKey]}
                        onChange={(e) => {
                          setRep(committeeKey, e.target.value);
                          clearFieldError(committeeKey);
                          clearFieldError('representativesCommittees');
                        }}
                      />
                    </RepCard>
                  );
                })}
              </RepresentativesBlock>
              </FormSection>
            )}
            {step === 5 && (
              <FormSection
                title={t('register.institution.step5')}
                spacing="lg"
              >
              <SectionHeading>{t('register.foundationActSection')}</SectionHeading>
              <FormAlert variant="info">
                {t('register.foundationActHint')}
              </FormAlert>
              <FileUpload
                onFileSelect={(file) => {
                  setFormData(prev => ({ ...prev, foundationAct: file }));
                  clearFieldError('foundationAct');
                }}
                onFileRemove={() => {
                  setFormData(prev => ({ ...prev, foundationAct: null }));
                }}
                currentFile={formData.foundationAct}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                maxSize={10 * 1024 * 1024}
                label={t('register.foundationActLabel')}
                hint={t('register.foundationActUploadHint')}
              />
              {hasAttemptedFinalSubmit && fieldErrors.foundationAct && (
                <Text size="sm" color="error" role="alert" style={{ marginBottom: spacing[3] }}>
                  {fieldErrors.foundationAct}
                </Text>
              )}
              {isResubmit && (
                <FormField
                  id="register-institution-applicant-note"
                  label={t('applicationStatus.applicantNote')}
                  textarea
                  name="applicantNote"
                  value={applicantNote}
                  helperText={t('applicationStatus.applicantNotePlaceholder')}
                  onChange={e => setApplicantNote(e.target.value)}
                />
              )}
              <SectionHeading>{t('register.declarationHeading')}</SectionHeading>
              <StatementBlock>{t('register.declarationStatement')}</StatementBlock>
              <FormRow>
                <FormField
                  label={t('register.declarationDateLabel')}
                  type="date"
                  value={formData.declarationDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, declarationDate: e.target.value }))}
                />
              </FormRow>
              <Text size="sm" color="secondary" style={{ marginTop: spacing[4], marginBottom: spacing[2] }}>
                {[
                  t('register.signatureDirector'),
                  t('register.signatureContactPerson'),
                  isRepresentativeStarted('rep1') ? t('register.signatureRep1') : null,
                  isRepresentativeStarted('rep2') ? t('register.signatureRep2') : null,
                  isRepresentativeStarted('rep3') ? t('register.signatureRep3') : null,
                ]
                  .filter(Boolean)
                  .join(' / ')}
              </Text>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="acceptedEthics"
                  checked={formData.acceptedEthics}
                  aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedEthics) || undefined}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, acceptedEthics: e.target.checked }));
                    clearFieldError('acceptedEthics');
                  }}
                />
                <CheckboxLabel htmlFor="acceptedEthics">{t('register.checkboxAcknowledgeForm')}</CheckboxLabel>
              </CheckboxContainer>
              {hasAttemptedFinalSubmit && fieldErrors.acceptedEthics && (
                <Text size="sm" color="error" role="alert" style={{ marginBottom: spacing[3] }}>
                  {fieldErrors.acceptedEthics}
                </Text>
              )}
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="acceptedGdpr"
                  checked={formData.acceptedGdpr}
                  aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedGdpr) || undefined}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, acceptedGdpr: e.target.checked }));
                    clearFieldError('acceptedGdpr');
                  }}
                />
                <CheckboxLabel htmlFor="acceptedGdpr">{t('register.checkboxGdpr')}</CheckboxLabel>
              </CheckboxContainer>
              {hasAttemptedFinalSubmit && fieldErrors.acceptedGdpr && (
                <Text size="sm" color="error" role="alert" style={{ marginBottom: spacing[3] }}>
                  {fieldErrors.acceptedGdpr}
                </Text>
              )}
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="acceptedDataTransfer"
                  checked={formData.acceptedDataTransfer}
                  aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedDataTransfer) || undefined}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, acceptedDataTransfer: e.target.checked }));
                    clearFieldError('acceptedDataTransfer');
                  }}
                />
                <CheckboxLabel htmlFor="acceptedDataTransfer">{t('register.checkboxDataTransfer')}</CheckboxLabel>
              </CheckboxContainer>
              {hasAttemptedFinalSubmit && fieldErrors.acceptedDataTransfer && (
                <Text size="sm" color="error" role="alert" style={{ marginBottom: spacing[3] }}>
                  {fieldErrors.acceptedDataTransfer}
                </Text>
              )}
              <Button variant="outline" type="button" onClick={() => setGdprModalOpen(true)} style={{ marginTop: spacing[4], marginBottom: spacing[4] }}>
                {t('register.gdprInfo')}
              </Button>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  id="acceptedStatement"
                  checked={formData.acceptedStatement}
                  aria-invalid={(hasAttemptedFinalSubmit && !!fieldErrors.acceptedStatement) || undefined}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, acceptedStatement: e.target.checked }));
                    clearFieldError('acceptedStatement');
                  }}
                />
                <CheckboxLabel htmlFor="acceptedStatement">{t('register.acceptedStatement')}</CheckboxLabel>
              </CheckboxContainer>
              {hasAttemptedFinalSubmit && fieldErrors.acceptedStatement && (
                <Text size="sm" color="error" role="alert" style={{ marginBottom: spacing[3] }}>
                  {fieldErrors.acceptedStatement}
                </Text>
              )}
              <Button variant="outline" type="button" onClick={() => setStatementGdprModalOpen(true)} style={{ marginTop: spacing[2] }}>
                {t('register.viewStatementAndGdpr')}
              </Button>
              </FormSection>
            )}
          </StepPanel>
          </WizardStepPanel>
          </Form>

          <Modal
            isOpen={committeesModalOpen}
            onClose={() => setCommitteesModalOpen(false)}
            title={t('register.aboutCommittees')}
            maxWidth="560px"
          >
            <Text size="sm" color="secondary" style={{ marginBottom: spacing[4] }}>
              {t('register.committeesInfoIntro')}
            </Text>
            <a href={t('register.committeesLink')} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, marginBottom: spacing[4], display: 'block' }}>
              {t('register.committeesLink')}
            </a>
            {committeesLoadError ? (
              <Text size="sm" color="error">{committeesLoadError}</Text>
            ) : uniqueCommitteeList.length === 0 ? (
              <Text size="sm" color="secondary">{t('register.committeesLoading')}</Text>
            ) : (
              <CommitteesList>
                {uniqueCommitteeList.map(c => (
                  <li key={c.id}>
                    {c.code ? `${c.code} – ` : ''}
                    {i18n.language === 'gr' ? c.nameGr : c.nameEn}
                  </li>
                ))}
              </CommitteesList>
            )}
          </Modal>

          <Modal
            isOpen={gdprModalOpen}
            onClose={() => setGdprModalOpen(false)}
            title={t('register.gdprInfo')}
            maxWidth="720px"
          >
            <GdprTabs>
              <GdprTab $active={gdprVariant === 'with'} onClick={() => setGdprVariant('with')}>
                {t('register.gdprWithNationalCommittee')}
              </GdprTab>
              <GdprTab $active={gdprVariant === 'without'} onClick={() => setGdprVariant('without')}>
                {t('register.gdprWithoutNationalCommittee')}
              </GdprTab>
            </GdprTabs>
            <StatementBlock style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {gdprVariant === 'with' ? t('register.gdprWithText') : t('register.gdprWithoutText')}
            </StatementBlock>
          </Modal>

          <Modal
            isOpen={statementGdprModalOpen}
            onClose={() => setStatementGdprModalOpen(false)}
            title={t('register.viewStatementAndGdpr')}
            maxWidth="720px"
          >
            <SectionHeading style={{ marginTop: 0 }}>{t('register.declarationHeading')}</SectionHeading>
            <StatementBlock>{t('register.declarationStatement')}</StatementBlock>
            <GdprTabs>
              <GdprTab $active={gdprVariant === 'with'} onClick={() => setGdprVariant('with')}>
                {t('register.gdprWithNationalCommittee')}
              </GdprTab>
              <GdprTab $active={gdprVariant === 'without'} onClick={() => setGdprVariant('without')}>
                {t('register.gdprWithoutNationalCommittee')}
              </GdprTab>
            </GdprTabs>
            <StatementBlock style={{ maxHeight: '40vh', overflowY: 'auto' }}>
              {gdprVariant === 'with' ? t('register.gdprWithText') : t('register.gdprWithoutText')}
            </StatementBlock>
          </Modal>
      </WizardProgress>
    </AuthWizardShell>
  );
};

export default RegisterInstitution;
